/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import Card from '@/components/card';
import BreadcrumbComponent from '@/components/breadcrumb';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  notification,
  Radio,
  Row,
  Select,
  Table,
  TimePicker,
} from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { IOrder, IOrderItem } from '@/interfaces/order';
import { PlusIcon } from '@heroicons/react/24/solid';
import { DeleteFilled } from '@ant-design/icons';
import { IStock } from '@/interfaces/stock';
import dayjs, { Dayjs } from 'dayjs';
import locale from 'antd/es/date-picker/locale/th_TH';
import { useMutation } from '@tanstack/react-query';
import { addOrder } from '@/adapters/order';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { round } from 'lodash';
import StockSelectorModal from '@/components/stock/stock-selector-modal';
import CustomerSelector from '@/components/selector/customer-selector';
import ShippingAddressSelector from '@/components/selector/shipping-address-selector';
import UserSelector from '@/components/selector/user-selector';
import { useAuth } from '@/contexts/AuthContext';
import { ICustomer } from '@/interfaces/customer';
import { getShippingTypeOptions } from '@/helpers/shippingType';
import { twMerge } from 'tailwind-merge';
import { mediaView } from '@/utils/mediaUtils';
import { EUserRole } from '@/enums/EUserRole';
import { getShippingFullAddress } from '@/utils/shippingAddressUtils';
import ConfirmApprove from '@/components/modal/confirm-approve';
import { IDataModal } from '@/interfaces/dataModal';
import { EOrderStatus } from '@/enums/EOrder';
import { DetailItem } from '@/components/dataItem/data-view-item';

const _modal: IDataModal<Partial<IOrder>> = {
  open: false,
};

const CrateOrder = () => {
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const [mainForm] = Form.useForm<Partial<IOrder>>();
  const [form] = Form.useForm();
  const [discount, setDiscount] = useState(0);
  const [vatRate, setVatRate] = useState(7);
  const [data, setData] = useState<Partial<IOrderItem>[]>([]);
  const [editingKey, setEditingKey] = useState<string>('');
  const [editingIndex, setEditingIndex] = useState<string>('');
  const [isAddStock, setIsAddStock] = useState(false);
  const [completedOrder, setCompletedOrder] =
    useState<IDataModal<Partial<IOrder>>>(_modal);

  const isEditing = (record: Partial<IOrderItem>) => record.key === editingKey;

  const edit = (record: Partial<IOrderItem>) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.key || '');
  };

  const save = async (key: string) => {
    try {
      const row = (await form.validateFields()) as Partial<IOrderItem>;
      const newData = [...data];
      const index = newData.findIndex(item => key === item.key || '');
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          unitPrice: round(Number(item.unitPrice || 0), 2),
          quantity: round(Number(item.quantity || 0), 2),
          ...row,
        });
        setData(newData);
        setEditingKey('');
        setEditingIndex('');
      } else {
        newData.push(row);
        setData(newData);
        setEditingIndex('');
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  interface EditableCellProps extends React.HTMLAttributes<any> {
    editing: boolean;
    dataIndex: string;
    title: string;
    inputType: 'number' | 'text';
    record: Partial<IOrderItem>;
    index: number;
  }

  const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
    record,
    editing,
    dataIndex,
    title,
    inputType,
    children,
    ...restProps
  }) => {
    const inputNode =
      inputType === 'number' ? (
        <InputNumber
          className="w-full"
          type="number"
          onBlur={() => save(record.key || '')}
          onPressEnter={() => save(record.key || '')}
          autoFocus
          controls={false}
          decimalSeparator={'.'}
          precision={2}
          style={{ textAlignLast: 'right' }}
        />
      ) : (
        <Input onBlur={() => save(record.key || '')} />
      );

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: `Please Input ${title}!`,
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const columns = [
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      width: 120,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'สินค้า',
      dataIndex: 'productName',
      className: 'font-bold',
    },
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      width: 120,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'เลขที่ใบเสนอราคา',
      dataIndex: 'qtNo',
      width: 140,
      className: 'font-bold',
      align: 'center',
      render: (_: number, rec: IOrderItem) => (
        <div className="flex flex-row gap-3 cursor-pointer justify-center">
          <div
            className={twMerge(
              !rec.qtNo && 'text-gray-400',
              rec.qtId && 'text-blue-600 underline',
            )}
            onClick={() => (rec.qtId ? mediaView({ id: rec.qtId }) : false)}
          >
            {_ ?? ''}
          </div>
        </div>
      ),
    },
    {
      title: 'จำนวน Package',
      width: 120,
      dataIndex: 'package',
      align: 'right',
      render: (_: number, rec: IOrderItem) =>
        formatNumber(round(rec.quantity / rec.conversion, 2), 2),
    },
    {
      title: 'หน่วย Package',
      width: 120,
      dataIndex: 'packageUnitName',
    },
    {
      title: 'จำนวน',
      width: 120,
      editable: editingIndex === 'quantity',
      dataIndex: 'quantity',
      className: 'font-bold text-green-700',
      align: 'right',
      render: (_: number, rec: IOrderItem) => (
        <div
          className="font-bold w-full cursor-pointer"
          onClick={() => onRowClick(rec, 'quantity')}
        >
          {formatNumber(_, 2)}
        </div>
      ),
    },
    {
      title: 'หน่วยขาย',
      width: 100,
      dataIndex: 'baseUnitName',
      className: 'font-bold',
    },
    {
      title: 'ราคา',
      editable: editingIndex === 'unitPrice',
      width: 120,
      dataIndex: 'unitPrice',
      align: 'right',
      className: 'font-bold text-blue-700',
      render: (_: number, rec: IOrderItem) => (
        <div
          className="font-bold w-full cursor-pointer"
          onClick={() => onRowClick(rec, 'unitPrice')}
        >
          {formatNumber(_, 2)}
        </div>
      ),
    },
    {
      title: 'รวมเงิน',
      width: 150,
      dataIndex: 'totalPrice',
      align: 'right',
      className: 'font-bold',
      render: (_: number, rec: IOrderItem) =>
        formatNumber(round(rec.quantity * (rec.unitPrice || 0), 2), 2),
    },
    {
      title: '',
      width: 40,
      dataIndex: 'action',
      align: 'center',
      render: (_: string, rec: IOrderItem) => (
        <DeleteFilled
          className="text-red-700"
          onClick={() => onDeleteOrderItem(rec)}
        />
      ),
    },
  ];

  const mergedColumns: any = columns.map(col => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: IOrderItem) => ({
        record,
        inputType: col.dataIndex === editingIndex ? 'number' : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const onAddOrderItem = (items: IStock[]) => {
    console.log('item IStock', items);
    const orderItems: Partial<IOrderItem>[] = items.map(i => {
      const orderItem: Partial<IOrderItem> = {
        ...i,
        key: uuidv4(),
        id: 0,
        stockId: i.id,
        quantity: i.quantity,
        remain: i.quantity,
        lotNumber: i.code,
        qtId: i.qtId,
        qtNo: i.qtNo,
      };
      return orderItem;
    });
    setData([...data, ...orderItems]);
    // setIsAddStock(false);
  };

  const onDeleteOrderItem = (item: IOrderItem) => {
    setData(prev => prev.filter(p => p.key !== item.key));
  };

  const onRowClick = (record: Partial<IOrderItem>, index: string) => {
    edit(record);
    setEditingIndex(index);
  };

  const mutation = useMutation({
    mutationFn: async (data: Partial<IOrder>) => await addOrder(data),
    mutationKey: ['fetchOrder'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        notification.success({
          message: 'บันทึกออเดอร์สำเร็จ',
        });
        navigate(`/order/${data?.data?.id}`);
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async (isDraft: boolean) => {
    mainForm
      .validateFields()
      .then(async (values: Partial<IOrder>) => {
        if (!data.length) {
          message.warning('กรุณาเลือกรายการ');
          return;
        }

        values.orderItems = data.map(d => {
          const orderItem: Partial<IOrderItem> = {
            id: 0,
            stockId: d.stockId,
            quantity: d.quantity,
            unitPrice: d.unitPrice,
            conversion: d.conversion,
            baseUnitId: d.baseUnitId,
            packageUnitId: d.packageUnitId,
            qtId: d.qtId,
            qtNo: d.qtNo,
          };
          return orderItem;
        });
        values.orderDate = dayjs(values.orderDate).toDate();
        values.dueDate = values.dueDate && dayjs(values.dueDate).toDate();
        values.vatRate = vatRate;
        values.discountAmount = discount;
        values.dueDateTime =
          (values.dueDateTime && dayjs(values.dueDateTime).format('HH:mm')) ||
          undefined;
        if (isDraft) {
          await mutation.mutateAsync(values);
        } else {
          values.status = EOrderStatus.APPROVED;
          setCompletedOrder({
            ...completedOrder,
            open: true,
            item: values,
          });
        }
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  useEffect(() => {
    if (userInfo?.id && userInfo.role === EUserRole.Employee) {
      mainForm.setFieldsValue({
        salesId: userInfo.id,
      });
    }
  }, [userInfo, mainForm]);

  const onCustomerChange = (customerInfo: ICustomer) => {
    mainForm.setFieldsValue({
      customerId: customerInfo.id,
      customerName: customerInfo.name,
      shippingAddressId: undefined,
      creditTerm: customerInfo.creditTerm,
      logisticRemark: customerInfo.logisticRemark,
      whRemark: customerInfo.whRemark,
      billingRemark: customerInfo.billingRemark,
      dueDate: dayjs(_orderDate).add(customerInfo.creditTerm, 'day'),
      salesId: customerInfo.salesId,
    });
  };

  const _orderDate = Form.useWatch('orderDate', mainForm);
  const _orderCreateDate = Form.useWatch('orderCreateDate', mainForm);
  const _customerId = Form.useWatch('customerId', mainForm);
  const _customerName = Form.useWatch('customerName', mainForm);
  const _shippingAddressId = Form.useWatch('shippingAddressId', mainForm);
  const _salesId = Form.useWatch('salesId', mainForm) || undefined;
  const _creditTerm = Form.useWatch('creditTerm', mainForm);
  const _shippingAddressText = Form.useWatch('shippingAddressText', mainForm);
  const _soNo = Form.useWatch('soNo', mainForm);
  const _poNo = Form.useWatch('poNo', mainForm);
  const _ivNo = Form.useWatch('ivNo', mainForm);

  let totalPrice = 0;
  let summaryPrice = 0;
  data?.forEach(({ quantity, unitPrice }) => {
    totalPrice += Number(round((quantity || 1) * (unitPrice || 0), 2));
  });
  const vatPrice = round(((totalPrice - discount) * vatRate) / 100, 2);
  summaryPrice = totalPrice - discount + vatPrice;

  const _isSameDay = dayjs(_orderDate).isSame(_orderCreateDate);

  return (
    <Card>
      <BreadcrumbComponent
        items={[
          {
            title: (
              <div
                className="cursor-pointer"
                onClick={() => navigate(`/order`)}
              >
                ออเดอร์
              </div>
            ),
          },
          {
            title: 'สร้างออเดอร์ใหม่',
          },
        ]}
      />
      <div className="flex w-full mt-5">
        <Form
          form={mainForm}
          layout="vertical"
          className="w-full"
          initialValues={{
            orderDate: dayjs().add(1, 'day'),
            orderCreateDate: dayjs().add(1, 'day'),
            // postponeRemark: `วันที่ในเอกสาร (${formatDate(dayjs(_orderCreateDate), dateFormatTH)}) อาจคลาดเคลื่อนจากวันที่ส่งจริง (${formatDate(dayjs(_orderDate), dateFormatTH)}) ทั้งนี้วันครบกำหนดชำระจะนับจากวันที่ทำการจัดส่งสินค้า เนื่องจาก `,
          }}
          autoComplete="off"
        >
          <Form.Item<IOrder> name="customerName" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item<IOrder> name="whRemark" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item<IOrder> name="logisticRemark" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item<IOrder> name="shipToRemark" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item<IOrder> name="billingRemark" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item<IOrder> name="shippingAddressText" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Row gutter={24}>
            <Col md={24} lg={16} xxl={18}>
              <Row gutter={12}>
                <Col xs={24} md={24} lg={12} xxl={8}>
                  <Form.Item
                    label="ลูกค้า"
                    name="customerId"
                    rules={[{ required: true, message: 'เลือกลูกค้า' }]}
                  >
                    <CustomerSelector handleSelectData={onCustomerChange} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={24} lg={8} xxl={8}>
                  <Form.Item<IOrder>
                    label="ที่จัดส่ง"
                    name="shippingAddressId"
                    rules={[{ required: true, message: 'เลือกที่จัดส่ง' }]}
                  >
                    <ShippingAddressSelector
                      value={_shippingAddressId}
                      customerId={_customerId}
                      handleSelectData={(id, item) => {
                        mainForm.setFieldsValue({
                          shippingAddressId: id,
                          shipToRemark: item?.remark,
                          shippingAddressText: getShippingFullAddress(item),
                          dueDateTime:
                            (item?.dueDateTime &&
                              dayjs(item?.dueDateTime, 'HH:mm')) ||
                            undefined,
                        });
                      }}
                    />
                    {_shippingAddressText && (
                      <div className="text-blue-700">
                        {_shippingAddressText}
                      </div>
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} md={24} lg={4} xxl={4}>
                  <Form.Item<IOrder>
                    label="Transport"
                    name="transportation"
                    rules={[{ required: true, message: 'เลือก Transport' }]}
                  >
                    <Select
                      allowClear
                      className="md:min-w-[150px]"
                      size="large"
                      placeholder="เลือก Transport"
                      options={getShippingTypeOptions()}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col xs={24} md={12} lg={6} xxl={4}>
                  <Form.Item<IOrder>
                    label="เลขที่ใบสั่งสินค้า (CO)"
                    name="soNo"
                  >
                    <Input size="large" placeholder="เลขที่ใบสั่งสินค้า" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={6} xxl={4}>
                  <Form.Item<IOrder>
                    label="เลขที่ใบแจ้งหนี้ (IV)"
                    name="ivNo"
                    // rules={[
                    //   {
                    //     pattern: /^[0-9-]+$/,
                    //     message:
                    //       'ระบุเฉพาะตัวเลขและเครื่องหมายขีด (-) เท่านั้น',
                    //   },
                    // ]}
                  >
                    <Input size="large" placeholder="เลขที่ใบแจ้งหนี้" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={6} xxl={4}>
                  <Form.Item<IOrder>
                    label="เลขที่อ้างอิงใบสั่งซื้อ (PO)"
                    name="poNo"
                  >
                    <Input size="large" placeholder="เลขที่ใบสั่งซื้อ" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={6} xxl={4}>
                  <Form.Item<IOrder> label="ส่งสินค้าภายใน" name="dueDateTime">
                    <TimePicker
                      size="large"
                      format="HH:mm"
                      value={dayjs(form.getFieldValue('dueDateTime'), 'HH:mm')}
                      minuteStep={30}
                      locale={locale}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col md={24} lg={8} xxl={6}>
              <Row gutter={12}>
                <Col xs={24} md={{ span: 12 }}>
                  <Form.Item<Partial<IOrder>>
                    label="ประเภทออเดอร์"
                    name="isSample"
                    rules={[
                      { required: true, message: 'กรุณาเลือกประเภทออเดอร์' },
                    ]}
                    initialValue={false}
                  >
                    <Radio.Group>
                      <Radio value={false}>สินค้าขาย</Radio>
                      <Radio value={true}>ตัวอย่าง</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col xs={24} md={{ span: 12 }}>
                  <Form.Item<Partial<IOrder>>
                    label="วันที่สั่งซื้อ"
                    name="orderCreateDate"
                    rules={[
                      { required: true, message: 'กรุณาเลือกวันที่สั่งซื้อ' },
                    ]}
                  >
                    <DatePicker
                      allowClear={false}
                      size="large"
                      className="w-full"
                      locale={locale}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item<Partial<IOrder>>
                    label="พนักงานขาย"
                    name="salesId"
                    rules={[{ required: true, message: 'เลือกพนักงานขาย' }]}
                  >
                    <UserSelector
                      value={_salesId}
                      role={EUserRole.Employee}
                      handleSelectData={id =>
                        mainForm.setFieldsValue({
                          salesId: id,
                        })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item<Partial<IOrder>>
                    label="วันที่ส่งสินค้า"
                    name="orderDate"
                    rules={[
                      { required: true, message: 'กรุณาเลือกวันที่ส่งสินค้า' },
                    ]}
                  >
                    <DatePicker
                      size="large"
                      className="w-full"
                      locale={locale}
                      allowClear={false}
                      onChange={d => {
                        mainForm.setFieldsValue({
                          dueDate: dayjs(d).add(
                            Number(_creditTerm || 0),
                            'day',
                          ),
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item<Partial<IOrder>>
                    label="กำหนดชำระ (วัน)"
                    name="creditTerm"
                    rules={[{ required: true, message: 'กำหนดชำระ (วัน)' }]}
                  >
                    <InputNumber
                      size="large"
                      className="w-full"
                      placeholder="กำหนดชำระ (วัน)"
                      onChange={v => {
                        mainForm.setFieldsValue({
                          dueDate: dayjs(_orderDate).add(v as number, 'day'),
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item<Partial<IOrder>>
                    label="ครบกำหนดชำระ"
                    name="dueDate"
                    rules={[
                      { required: true, message: 'กรุณาเลือกครบกำหนดชำระ' },
                    ]}
                  >
                    <DatePicker
                      size="large"
                      className="w-full"
                      locale={locale}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </div>
      <div className="flex w-full mt-5">
        <Form form={form} component={false}>
          <Table
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            locale={{
              emptyText: 'เพิ่มรายการใหม่',
            }}
            size="middle"
            bordered
            dataSource={data}
            columns={mergedColumns}
            className="w-full"
            rowClassName="editable-row"
            pagination={false}
            scroll={{ x: 1200 }}
            summary={() => {
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={9}
                      className="font-bold items-end"
                      align="right"
                    >
                      รวมเงิน
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={7}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    >
                      {formatNumber(totalPrice, 2)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={9}
                      colSpan={1}
                      className="font-bold items-end "
                      align="right"
                    />
                  </Table.Summary.Row>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={9}
                      className="font-bold items-end"
                      align="right"
                    >
                      ส่วนลด (บาท)
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={5}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    >
                      <InputNumber
                        className="w-full"
                        value={discount}
                        style={{ textAlignLast: 'right' }}
                        controls={false}
                        decimalSeparator="."
                        formatter={value =>
                          `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        }
                        parser={value =>
                          value?.replace(/\$\s?|(,*)/g, '') as unknown as number
                        }
                        precision={2}
                        onChange={value => {
                          setDiscount(value || 0);
                        }}
                        min={0}
                      />
                    </Table.Summary.Cell>{' '}
                    <Table.Summary.Cell
                      index={9}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    />
                  </Table.Summary.Row>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={9}
                      className="font-bold items-end"
                      align="right"
                    >
                      ยอดรวมหลังหักส่วนลด
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={7}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    >
                      {formatNumber(totalPrice - discount, 2)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={9}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    />
                  </Table.Summary.Row>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={9}
                      className="font-bold items-end"
                      align="right"
                    >
                      <InputNumber
                        value={vatRate}
                        onChange={value => {
                          setVatRate(value || 0);
                        }}
                        min={0}
                        suffix="%"
                      />
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={7}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    >
                      {formatNumber(vatPrice, 2)}
                    </Table.Summary.Cell>{' '}
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    />
                  </Table.Summary.Row>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={9}
                      className="font-bold items-end"
                      align="right"
                    >
                      ยอดสุทธิ
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={7}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    >
                      {formatNumber(summaryPrice, 2)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    />
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Form>
      </div>
      <div className="flex justify-end mt-3">
        <Button
          disabled={!_customerId}
          type="default"
          className="text-blue-700 font-bold"
          onClick={() => setIsAddStock(true)}
        >
          <PlusIcon className="w-3 h-3" /> เพิ่มรายการ
        </Button>
        <StockSelectorModal
          handleCancel={() => setIsAddStock(false)}
          handleOk={onAddOrderItem}
          open={isAddStock}
          exists={[]}
          customerId={Number(_customerId)}
        />
      </div>
      <div className="flex w-full">
        <Form form={mainForm} layout="vertical" className="w-full">
          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item label="หมายเหตุ" name="remark">
                <Input.TextArea rows={3} placeholder="หมายเหตุ" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item<IOrder>
                label="หมายเหตุเลื่อนจัดส่ง"
                name="postponeRemark"
              >
                <Input.TextArea
                  disabled={_isSameDay}
                  rows={3}
                  placeholder="หมายเหตุเลื่อนจัดส่ง"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      <div className="flex justify-end mt-5 gap-2">
        <Button
          ghost
          type="primary"
          htmlType="submit"
          size="large"
          className=" font-bold min-w-[150px]"
          loading={mutation.isPending}
          onClick={() => handleUpdate(true)}
        >
          บันทึก
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          className=" font-bold min-w-[150px]"
          loading={mutation.isPending}
          onClick={() => handleUpdate(false)}
        >
          ยืนยันการจัดสินค้า
        </Button>
      </div>
      <ConfirmApprove
        content={`คุณต้องการยืนยันการจัดสินค้า ใช่หรือไม่`}
        handleCancel={() => {
          setCompletedOrder({ ..._modal });
        }}
        handleOk={async () => {
          await mutation.mutateAsync(completedOrder?.item as Partial<IOrder>);
        }}
        open={completedOrder?.open}
        title={`ยืนยันการจัดสินค้า`}
        showInfo
        info={
          <div className="flex flex-col justify-end mt-2 gap-2">
            <DetailItem
              customLabelClass="md:min-w-[80px]"
              label="ลูกค้า"
              detail={_customerName}
            />
            <DetailItem
              customLabelClass="md:min-w-[80px]"
              label="วันที่สั่งซื้อ"
              detail={formatDate(_orderDate as Dayjs)}
            />
            <DetailItem
              customLabelClass="md:min-w-[80px]"
              label="วันที่สั่งซื้อ"
              detail={formatDate(_orderDate as Dayjs)}
            />
            <DetailItem
              customLabelClass="md:min-w-[80px]"
              label="CO"
              detail={_soNo}
            />
            <DetailItem
              customLabelClass="md:min-w-[80px]"
              label="IV"
              detail={_ivNo}
            />
            <DetailItem
              customLabelClass="md:min-w-[80px]"
              label="PO"
              detail={_poNo}
            />
            <DetailItem
              customLabelClass="md:min-w-[80px]"
              label="ออเดอร์"
              detail={`${data?.length} รายการ`}
            />
            <div className="flex flex-col border border-red-400 rounded-lg p-2 text-bold w-full bg-red-50">
              <DetailItem
                customMainClass="flex-row"
                customLabelClass="flex justify-end min-w-[140px]  text-sm"
                label="รวมเงิน"
                detail={formatNumber(totalPrice, 2)}
                customClass="flex justify-end w-full text-black font-bold  text-sm"
              />
              <DetailItem
                customMainClass="flex-row"
                customLabelClass="flex justify-end min-w-[140px]    text-sm"
                label="ส่วนลด"
                detail={formatNumber(Number(discount), 2)}
                customClass="flex justify-end w-full text-black font-bold  text-sm"
              />
              <DetailItem
                customMainClass="flex-row"
                customLabelClass="flex justify-end min-w-[140px]    text-sm"
                label="รวมเงินหลังหักส่วนลด"
                detail={formatNumber(
                  Number(totalPrice - Number(discount || 0)),
                  2,
                )}
                customClass="flex justify-end w-full text-black font-bold  text-sm"
              />
              <DetailItem
                customMainClass="flex-row"
                customLabelClass="flex justify-end min-w-[140px]    text-sm"
                label={`${vatRate} %`}
                detail={formatNumber(vatPrice, 2)}
                customClass="flex justify-end w-full text-black font-bold  text-sm"
              />
              <DetailItem
                customMainClass="flex-row"
                customLabelClass="flex justify-end min-w-[140px]   text-sm"
                label="ยอดสุทธิ"
                detail={formatNumber(summaryPrice, 2)}
                customClass="flex justify-end w-full text-black font-bold  text-sm"
              />
            </div>
          </div>
        }
      />
    </Card>
  );
};

export default CrateOrder;
